import { Box } from '@mui/material';
import type { ReactNode } from 'react';
import { PanelCard, StatHeader } from './PanelCard';
import { RingGauge, LegendDot } from './Ring';
import { usePanelTokens } from './tokens';

export interface ScoreGaugeCardProps {
  title: ReactNode;
  info?: string;
  /** The big score in the gauge center (e.g. 89). */
  score: number;
  /** Gauge fill 0–100; defaults to `score`. */
  percent?: number;
  /** Header values. */
  statusValue: ReactNode;
  statusLabel?: ReactNode;
  secondaryValue?: ReactNode;
  secondaryLabel?: ReactNode;
  /** Gauge color — defaults to positive/green. */
  color?: string;
  legendLabel?: ReactNode;
  legendValue?: ReactNode;
}

/** Single-value circular score gauge (the green "89" widget). */
export function ScoreGaugeCard({
  title,
  info,
  score,
  percent,
  statusValue,
  statusLabel = 'Status',
  secondaryValue,
  secondaryLabel,
  color,
  legendLabel,
  legendValue,
}: ScoreGaugeCardProps) {
  const t = usePanelTokens();
  const arc = color ?? t.positive;
  return (
    <PanelCard title={title} info={info}>
      <StatHeader
        usedValue={statusValue}
        usedLabel={statusLabel}
        totalValue={secondaryValue}
        totalLabel={secondaryLabel}
        usedColor={arc}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.25, mt: 2 }}>
        <RingGauge
          value={percent ?? score}
          color={arc}
          trackColor={t.positiveSoft}
          centerTop={score}
        />
        {(legendLabel || legendValue) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1 }}>
            {legendLabel && <LegendDot color={arc}>{legendLabel}</LegendDot>}
            {legendValue && (
              <Box sx={{ fontSize: '0.75rem', color: t.label }}>{legendValue}</Box>
            )}
          </Box>
        )}
      </Box>
    </PanelCard>
  );
}
